import { supabase } from './supabase'

/**
 * @mentions — the client half.
 *
 * ── THE BODY CARRIES AN ID, NEVER A NAME ────────────────────────────────────
 *
 * A mention is stored as `@{<uuid>}` and the display name is resolved at read
 * time from the `mentions` map that comments_for() returns. That is 023's rule
 * for the notification outbox — hold the id, render the words late — applied
 * one layer up, and it buys three things a stored name cannot:
 *
 *   * somebody renames and every sentence that mentioned them is still right
 *   * somebody leaves and their name is not embedded in other people's prose
 *   * a reader who may not see that person gets "@someone", not a leak
 *
 * ── THE SERVER NEVER MATCHES A NAME AND NEITHER DOES THIS ───────────────────
 *
 * The tempting build is a regex for @word resolved against display names. It
 * is wrong here for reasons this project has already paid for: the roster holds
 * 932 people, TWO called James Miller, one called "test test", and one whose
 * preferred-name field holds a whole name with a space in it. Name matching
 * picks the wrong James silently, and does nothing at all on a typo — the
 * writer believes they tagged somebody and that person is never told.
 *
 * app.mentioned_person_ids() reads tokens and only tokens, and the 059 suite's
 * assertions 3 and 4 fail if that ever changes.
 *
 * ── THE TOKEN IS NEVER SHOWN TO A HUMAN ─────────────────────────────────────
 *
 * The person typing sees "@Jamie Sharp" because the picker writes the token and
 * the composer renders over it; the person reading sees a chip. Nobody is ever
 * asked to look at a uuid. If a surface ever does show one, that surface is not
 * calling splitMentions.
 */

/** Matches one `@{uuid}` token. Global, so it must not be reused across calls
 *  without resetting lastIndex — hence a fresh RegExp each time. */
const TOKEN = () =>
  /@\{([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}/g

export type MentionMap = Record<string, string>

export type MentionablePerson = {
  personId: string
  displayName: string
}

/**
 * A page of people AND how many there were. REC-54, 088.
 *
 * THE SHAPE IS DELIBERATELY NOT AN ARRAY, and that is the whole point of
 * changing it. A bare array is indistinguishable from the complete answer, so
 * every one of the four surfaces that shares `mentionable_people` rendered a
 * truncated list as though it were everything, and nothing in any of their
 * types said otherwise. Returning `{people, total}` makes a caller that ignores
 * the total do so on purpose.
 *
 * `total` is what matched BEFORE the limit, counted by the database in the same
 * statement. A client can never compute it — counting `people` gives the page
 * size, which is the bug wearing a number — and fetching everything to count it
 * is standing rule 1's failure with the work moved into the browser.
 *
 * It lives here rather than in `stars.ts` because both wrappers of that one RPC
 * need it and a second copy of the type is a second answer (standing rule 11).
 */
export type PeoplePage<T> = {
  people: T[]
  total: number
}

/** The token a picker inserts. The ONE place this string is built. */
export function mentionToken(personId: string): string {
  return `@{${personId}}`
}

/* ── THE DRAFT: A NAME ON SCREEN, AN ID ON THE WIRE ──────────────────────────
 *
 * David, 13 Sep: "when we @ someone it inserts their GUID in {} until posted."
 * It did, and MentionBox's own header claimed otherwise — it said the writer
 * "sees @Jamie Sharp because the picker writes the token and the composer
 * renders over it", and nothing rendered over anything. The person composing
 * was looking at `@{6f1c…}` in the middle of their sentence.
 *
 * The textarea now holds what a person would type — `@Jamie Sharp` — and the
 * ids live beside it as SPANS. `toStorage` swaps them back at submit, so the
 * body that reaches the database is unchanged and every server-side rule
 * (app.mentioned_person_ids, the never-match-a-name guarantee, assertions 3
 * and 4 of the 059 suite) is untouched. This is a presentation change that
 * does not reach the schema, which is the only reason it is cheap.
 *
 * ── WHY SPANS AND NOT A NAME LOOKUP AT SUBMIT ───────────────────────────────
 *
 * The obvious shortcut is to keep a name→id map and swap `@Jamie Sharp` for a
 * token at submit. It is wrong on this roster for the reason the whole token
 * design exists: there are TWO James Millers. A name is not a key, so
 * resolving by name at submit would silently tag the wrong one — the exact
 * failure the picker was built to prevent, reintroduced one layer up.
 *
 * A span is a position, and a position is unambiguous.
 *
 * ── AND EDITING A MENTION UNMAKES IT, DELIBERATELY ──────────────────────────
 *
 * `reconcile` drops any span the edit touched. Backspace into "@Jamie Sharp"
 * and it becomes ordinary text that mentions nobody — which is correct and is
 * the same rule as before: a mention that was not picked was not made. The
 * alternative (repairing the span, or refusing the keystroke) would leave the
 * writer looking at text that says one name while addressing a person they can
 * no longer see.
 */

export type MentionSpan = {
  /** Index of the '@'. */
  start: number
  /** Exclusive end — start + ('@' + displayName).length at insert time. */
  end: number
  personId: string
}

/** What the writer is looking at, plus who the marked-up parts point to. */
export type MentionDraft = {
  text: string
  spans: MentionSpan[]
}

export const EMPTY_DRAFT: MentionDraft = { text: '', spans: [] }

/**
 * Put a chosen person into the draft, replacing the `@qu` fragment being typed.
 *
 * Returns the new draft and where the caret should land — the caller cannot
 * compute that itself without re-deriving the name's length, and two answers to
 * "where is the caret" is how it ends up in the middle of the name.
 */
export function insertMention(
  draft: MentionDraft,
  from: number,
  caret: number,
  person: MentionablePerson,
): { draft: MentionDraft; caret: number } {
  const label = `@${person.displayName}`
  // One trailing space so the next word does not re-open the menu.
  const inserted = `${label} `
  const text = draft.text.slice(0, from) + inserted + draft.text.slice(caret)
  const delta = inserted.length - (caret - from)

  // Existing spans move exactly as the text around them moved. Anything that
  // overlapped the fragment being replaced is gone with it.
  const spans = draft.spans
    .filter((s) => s.end <= from || s.start >= caret)
    .map((s) => (s.start >= caret ? { ...s, start: s.start + delta, end: s.end + delta } : s))

  spans.push({ start: from, end: from + label.length, personId: person.personId })
  spans.sort((a, b) => a.start - b.start)

  return { draft: { text, spans }, caret: from + inserted.length }
}

/**
 * Carry the spans across an arbitrary edit — typing, pasting, deleting, undo.
 *
 * The diff is the cheapest one that is correct for a single contiguous edit,
 * which is what a textarea produces: match the common prefix, match the common
 * suffix, and everything between them is what changed. A span wholly before the
 * change keeps its position, a span wholly after it shifts, and a span the
 * change TOUCHED is dropped.
 *
 * Dropping is the conservative direction and the one that cannot lie: a span
 * that no longer covers the name it was created for would render a chip over
 * the wrong words and post an id for a person whose name is not there.
 */
export function reconcile(draft: MentionDraft, nextText: string): MentionDraft {
  const a = draft.text
  const b = nextText
  if (a === b) return draft

  const max = Math.min(a.length, b.length)
  let p = 0
  while (p < max && a[p] === b[p]) p++
  let s = 0
  while (s < max - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s++

  // The replaced range in the OLD text, and how much longer the new one is.
  const from = p
  const to = a.length - s
  const delta = b.length - a.length

  const spans = draft.spans
    .filter((sp) => sp.end <= from || sp.start >= to)
    .map((sp) => (sp.start >= to ? { ...sp, start: sp.start + delta, end: sp.end + delta } : sp))

  return { text: b, spans }
}

/**
 * The body that goes to the database: names swapped back for `@{uuid}` tokens.
 *
 * Right to left, so replacing one span cannot move the next one's indices.
 * A span whose text no longer starts with '@' is skipped rather than trusted —
 * reconcile should make that impossible, and a body is not the place to find
 * out it was wrong.
 */
export function toStorage(draft: MentionDraft): string {
  let out = draft.text
  for (const s of [...draft.spans].sort((x, y) => y.start - x.start)) {
    if (draft.text[s.start] !== '@') continue
    out = out.slice(0, s.start) + mentionToken(s.personId) + out.slice(s.end)
  }
  return out
}

/**
 * A body split into text and mentions, ready to render.
 *
 * Returns segments rather than HTML on purpose: this app does not set
 * dangerouslySetInnerHTML anywhere, and a feed of ~900 people's posts is not
 * where to start. The caller maps segments to elements.
 *
 * An id with no entry in `map` renders as "someone" — the deliberate outcome
 * for a leaver, a suspended colleague, or somebody outside the reader's
 * tenancy. It is never left as a raw uuid.
 */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; personId: string; name: string }

export function splitMentions(body: string, map: MentionMap): Segment[] {
  const out: Segment[] = []
  const re = TOKEN()
  let last = 0
  let m: RegExpExecArray | null

  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: body.slice(last, m.index) })
    out.push({ kind: 'mention', personId: m[1], name: map[m[1]] ?? 'someone' })
    last = m.index + m[0].length
  }
  if (last < body.length) out.push({ kind: 'text', text: body.slice(last) })
  return out
}

/**
 * The same body with every token replaced by "@Name" as plain text.
 *
 * For places that need a string rather than nodes — a title attribute, a
 * notification preview, a confirm dialog. Never for rendering the thread, which
 * wants the chip.
 */
export function flattenMentions(body: string, map: MentionMap): string {
  return body.replace(TOKEN(), (_, id: string) => `@${map[id] ?? 'someone'}`)
}

/**
 * What the person is part-way through typing, if the caret sits in an @ word.
 *
 * Returns the query and where it starts, or null. The rules are deliberately
 * tight, because an @ that opens a menu every time somebody types an email
 * address is worse than no feature:
 *
 *   * the @ must start a word (line start, or after whitespace)
 *   * the first character after the @ is not whitespace — "@ " is punctuation
 *   * SPACES ARE ALLOWED, up to three of them, one at a time (a full name:
 *     "david cl", "David Alejandro Masias Cornejo"), but a newline, a tab or
 *     two spaces in a row end it
 *   * at most 40 characters, so a long paste cannot hold the menu open
 *
 * ── A SPACE NO LONGER CLOSES THE MENU (David and Chandra, 25 Sep) ────────────
 *
 * Until today a space ended the query, and this comment called that "the
 * accepted cost of not having a menu open over every sentence". Chandra hit the
 * cost at once: "thanks @david" and then a space, and the list of seventeen
 * Davids vanished before she could narrow it by surname — and somebody who
 * does not remember a colleague's surname well cannot get past the first name
 * at all. The server has always matched a substring of the FULL name
 * (`mentionable_people`), so "david c" finds David Clark and David Collins;
 * only this function stood in the way.
 *
 * What stops the menu hanging over the rest of the sentence is now the ANSWER,
 * not the keystroke: MentionBox hides the menu once a query with a space in it
 * matches nobody ("@david thanks for that" matches nobody after "thanks"), so
 * typing on past a name costs nothing. Enter only picks while rows are on
 * screen, so it goes back to being a newline at the same moment.
 */
export function activeMentionQuery(
  value: string,
  caret: number,
): { query: string; from: number } | null {
  const upto = value.slice(0, caret)
  const at = upto.lastIndexOf('@')
  if (at < 0) return null
  if (at > 0 && !/\s/.test(upto[at - 1])) return null

  const query = upto.slice(at + 1)
  if (/^\s/.test(query)) return null
  if (/[\r\n\t]|  /.test(query)) return null
  if ((query.match(/ /g) ?? []).length > 3) return null
  if (query.length > 40) return null
  return { query, from: at }
}

/**
 * How many people any person list asks for (25 Sep).
 *
 * The server's own ceiling (`mentionable_people` clamps to 25), so every list
 * gets everything it is allowed and scrolls inside its 20rem box. Six was
 * small enough that seventeen Davids read as a list you could not scroll;
 * PickerFoot still says "Showing 25 of 60" past the ceiling, because a page
 * must say it is a page (REC-54). ONE number for all four surfaces.
 */
export const PEOPLE_PAGE = 25

/**
 * Who can be mentioned.
 *
 * SECURITY INVOKER on the server, and it adds no exposure that `person`'s own
 * RLS did not already grant: every active member can already read every active
 * colleague's row. This is NOT people_and_roles, which 043 locked to
 * admin.roles because that one carries who moderates and who administers.
 */
export async function searchMentionable(
  query: string,
  limit = PEOPLE_PAGE,
): Promise<PeoplePage<MentionablePerson>> {
  const { data, error } = await supabase.rpc('mentionable_people', {
    p_query: query || null,
    p_limit: limit,
  })
  if (error) throw error
  const rows = (data ?? []) as Record<string, unknown>[]
  return {
    people: rows.map((r) => ({
      personId: r.person_id as string,
      displayName: r.display_name as string,
    })),
    // Off the first row, because 088 puts the same number on every row of one
    // call and its suite asserts they cannot disagree. No rows means nothing
    // matched, and nothing matched is not a page.
    total: rows.length ? Number(rows[0].total_matches) : 0,
  }
}
